import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "../ui/button";
import { api, formatCOP, formatApiError } from "../../lib/apiClient";
import { useN2Offer } from "../../lib/useN2Offer";

const OFFER_STATES = new Set(["nivel1_delivered", "nivel2_offer_sent"]);

export function N2OfferCard({ caseId, state, isOwner }) {
  const offer = useN2Offer();
  const [contact, setContact] = useState(null);
  const [sending, setSending] = useState(false);
  const visible = OFFER_STATES.has(state);

  useEffect(() => {
    if (!visible || !isOwner) return;
    api.get(`/cases/${caseId}/contact-commercial-n2`).then(({ data }) => setContact(data?.request || data)).catch(() => {});
  }, [caseId, visible, isOwner]);

  const requestContact = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/contact-commercial-n2`);
      setContact(data?.request || data || { status: "pending" });
      toast.success("Solicitud enviada. Un asesor te contactará en 1-2 días hábiles.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo enviar la solicitud");
    } finally {
      setSending(false);
    }
  };

  if (!visible) return null;
  const resume = false;
  return (
    <section className="mt-6 relative overflow-hidden border border-primary/40 bg-white" data-testid="n2-offer-card">
      <div className="absolute inset-y-0 left-0 w-1 bg-primary" />
      <div className="p-6 md:p-8 grid md:grid-cols-5 gap-6">
        <div className="md:col-span-3">
          <p className="text-[11px] uppercase tracking-[0.22em] text-primary font-medium mb-2">Siguiente paso · Nivel 2</p>
          <h3 className="font-serif text-2xl text-gray-900 mb-2" data-testid="n2-offer-title">{offer.title}</h3>
          {offer.subtitle && <p className="text-sm text-gray-600 mb-4">{offer.subtitle}</p>}
          <ul className="space-y-2" data-testid="n2-offer-deliverables">
            {(offer.deliverables || []).map((d, i) => (
              <li key={d.label} className="flex gap-2 text-sm" data-testid={`n2-offer-deliverable-${i}`}>
                <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span><strong className="text-gray-900">{d.label}.</strong> <span className="text-gray-600">{d.summary}</span></span>
              </li>
            ))}
          </ul>
          {offer.delivery_note && <p className="text-[11px] text-gray-500 mt-4">{offer.delivery_note}</p>}
        </div>
        <div className="md:col-span-2 md:border-l md:border-gray-200 md:pl-6 flex flex-col justify-center">
          <p className="text-[11px] uppercase tracking-widest text-gray-500">Con revisión humana</p>
          <p className="font-serif text-4xl text-gray-900 my-1" data-testid="n2-offer-price">{formatCOP(offer.price_reviewed_cop)}</p>
          <p className="text-xs text-gray-500 mb-4">
            Opción automática sin revisión: <span data-testid="n2-offer-price-auto">{formatCOP(offer.price_auto_cop)}</span>
          </p>
          {isOwner && (
            <Button asChild className="rounded-sm h-11 transition-transform hover:-translate-y-0.5" data-testid="n2-offer-cta">
              <Link to={resume ? `/cases/${caseId}/payment-n2` : `/cases/${caseId}/nivel2-request`}>
                {resume ? "Continuar al pago" : "Solicitar Kit Documental"}
              </Link>
            </Button>
          )}
          {isOwner && (contact?.status === "pending" ? (
            <p className="text-xs text-emerald-700 mt-3" data-testid="n2-offer-contact-pending">Solicitud de contacto enviada — te contactaremos pronto.</p>
          ) : (
            <Button variant="outline" onClick={requestContact} disabled={sending} className="rounded-sm h-10 mt-2 border-primary text-primary" data-testid="n2-offer-contact-btn">
              {sending ? "Enviando…" : "Prefiero hablar con un asesor"}
            </Button>
          ))}
          <p className="text-[11px] text-gray-400 mt-2">El pago se realiza en la siguiente pantalla. Sin cobro hasta confirmar.</p>
        </div>
      </div>
    </section>
  );
}
